import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

import { authApi } from '@/services/api'
import { Agent, Organization, Store } from '@/types'
import { Capabilities, capabilitiesForPlatform } from '@/lib/capabilities'

// ============================================================
// TYPES
// ============================================================

interface WhatsAppInfo {
  phoneNumber: string
  displayName: string | null
  status?: string
}

interface StoreWithWhatsApp extends Store {
  whatsapp: WhatsAppInfo | null
  permissions?: string[]
}

interface AuthContextType {
  user: Agent | null
  organization: Organization | null
  stores: StoreWithWhatsApp[]
  activeStoreId: string | null
  activeStore: StoreWithWhatsApp | null
  accessibleStoreIds: string[]
  capabilities: Capabilities
  loading: boolean
  isUnifiedView: boolean
  login: (email: string, password: string, storeId?: string) => Promise<void>
  logout: () => void
  refreshToken: () => Promise<void>
  switchStore: (storeId: string | null) => Promise<void>
  hasStoreAccess: (storeId: string) => boolean
  hasMultipleStores: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

// ============================================================
// AUTH PROVIDER
// ============================================================

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<Agent | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [stores, setStores] = useState<StoreWithWhatsApp[]>([])
  const [activeStoreId, setActiveStoreId] = useState<string | null>(null)
  const [accessibleStoreIds, setAccessibleStoreIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  // Derived state
  const activeStore = stores.find(s => s.id === activeStoreId) || null
  const isUnifiedView = activeStoreId === null
  const hasMultipleStores = stores.length > 1

  // Feature capabilities derived from the active (or first) store's platform.
  // Mirrors the backend resolver, so the UI shows commerce modules only for
  // commerce workspaces and the core only for STANDALONE workspaces.
  const capabilities = capabilitiesForPlatform(
    (activeStore || stores[0] as any)?.platform
  )

  // Load user on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      loadUser()
    } else {
      setLoading(false)
    }
  }, [])

  // Load user data from /auth/me
  const loadUser = async () => {
    try {
      const response = await authApi.me()
      const data = response.data.data

      setUser(data.agent)
      setOrganization(data.organization || null)
      setStores(data.stores || [])
      setActiveStoreId(data.activeStoreId || null)
      setAccessibleStoreIds(data.accessibleStoreIds || data.stores?.map((s: any) => s.id) || [])
    } catch (error) {
      console.error('Failed to load user:', error)
      localStorage.removeItem('auth_token')
    } finally {
      setLoading(false)
    }
  }

  // Login
  const login = async (email: string, password: string, storeId?: string) => {
    try {
      const response = await authApi.login({ email, password, storeId })
      const data = response.data.data

      localStorage.setItem('auth_token', data.token)

      setUser(data.agent)
      setOrganization(data.organization || null)
      setStores(data.stores || [])
      setActiveStoreId(data.activeStoreId || null)
      setAccessibleStoreIds(data.stores?.map((s: any) => s.id) || [])

      toast.success('Login successful!')
      navigate('/dashboard')
    } catch (error: any) {
      const message = error.response?.data?.error?.message || 'Login failed'
      toast.error(message)
      throw error
    }
  }

  // Logout
  const logout = useCallback(() => {
    localStorage.removeItem('auth_token')
    setUser(null)
    setOrganization(null)
    setStores([])
    setActiveStoreId(null)
    setAccessibleStoreIds([])
    navigate('/login')
    toast.success('Logged out successfully')
  }, [navigate])

  // Refresh token
  const refreshToken = async () => {
    try {
      const response = await authApi.refresh()
      localStorage.setItem('auth_token', response.data.data.token)
    } catch (error) {
      console.error('Failed to refresh token:', error)
      logout()
    }
  }

  // Switch store
  const switchStore = async (storeId: string | null) => {
    try {
      const response = await authApi.switchStore({ storeId })
      const data = response.data.data

      localStorage.setItem('auth_token', data.token)
      setActiveStoreId(data.activeStoreId)

      if (data.store) {
        // Update the store in our stores array if needed
        setStores(prev =>
          prev.map(s => s.id === data.store.id ? { ...s, ...data.store } : s)
        )
      }

      toast.success(data.message || (storeId ? 'Switched store' : 'Switched to unified view'))
    } catch (error: any) {
      const message = error.response?.data?.error?.message || 'Failed to switch store'
      toast.error(message)
      throw error
    }
  }

  // Check if user has access to a specific store
  const hasStoreAccess = useCallback((storeId: string) => {
    return accessibleStoreIds.includes(storeId)
  }, [accessibleStoreIds])

  const value: AuthContextType = {
    user,
    organization,
    stores,
    activeStoreId,
    activeStore,
    accessibleStoreIds,
    capabilities,
    loading,
    isUnifiedView,
    login,
    logout,
    refreshToken,
    switchStore,
    hasStoreAccess,
    hasMultipleStores
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// ============================================================
// HOOK
// ============================================================

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
